/**
 * Firestore Helper Utilities
 *
 * Centralized path builders and converters for Firestore operations.
 * Ensures consistent collection paths and prevents typos.
 */

import {
  collection,
  doc,
  CollectionReference,
  DocumentReference,
  Timestamp
} from 'firebase/firestore';
import { firestore } from '../services/firebase';

/**
 * Get user's recipes collection reference
 * Path: users/{userId}/recipes
 *
 * @param userId - Firebase Auth UID
 * @returns CollectionReference to user's recipes
 */
export const getUserRecipesCollection = (userId: string): CollectionReference => {
  return collection(firestore, 'users', userId, 'recipes');
};

/**
 * Get specific recipe document reference
 * Path: users/{userId}/recipes/{recipeId}
 *
 * @param userId - Firebase Auth UID
 * @param recipeId - Recipe document ID
 * @returns DocumentReference to specific recipe
 */
export const getRecipeDocRef = (userId: string, recipeId: string): DocumentReference => {
  return doc(firestore, 'users', userId, 'recipes', recipeId);
};

/**
 * Get user document reference
 * Path: users/{userId}
 *
 * @param userId - Firebase Auth UID
 * @returns DocumentReference to user document
 */
export const getUserDocRef = (userId: string): DocumentReference => {
  return doc(firestore, 'users', userId);
};

/**
 * Get user settings document reference
 * Path: users/{userId}/settings/preferences
 *
 * @param userId - Firebase Auth UID
 * @returns DocumentReference to user settings
 */
export const getUserSettingsDocRef = (userId: string): DocumentReference => {
  return doc(firestore, 'users', userId, 'settings', 'preferences');
};

/**
 * Convert Firestore Timestamp to Date object
 * Useful for displaying dates in the UI
 *
 * @param timestamp - Firestore Timestamp or undefined
 * @returns Date object or null if timestamp is undefined
 */
export const timestampToDate = (timestamp: Timestamp | undefined): Date | null => {
  return timestamp ? timestamp.toDate() : null;
};

/**
 * Convert Date to Firestore Timestamp
 *
 * @param date - JavaScript Date object
 * @returns Firestore Timestamp
 */
export const dateToTimestamp = (date: Date): Timestamp => {
  return Timestamp.fromDate(date);
};

/**
 * Server timestamp placeholder for writes
 * Use this when creating or updating documents to let Firestore set the timestamp
 */
export { serverTimestamp } from 'firebase/firestore';

/**
 * Format timestamp for display
 *
 * @param timestamp - Firestore Timestamp
 * @returns Formatted date string (e.g., "Jan 15, 2025")
 */
export const formatTimestamp = (timestamp: Timestamp | undefined): string => {
  const date = timestampToDate(timestamp);
  if (!date) {
    return 'Unknown';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

/**
 * Format timestamp with time for display
 *
 * @param timestamp - Firestore Timestamp
 * @returns Formatted date and time string (e.g., "Jan 15, 2025 at 3:45 PM")
 */
export const formatTimestampWithTime = (timestamp: Timestamp | undefined): string => {
  const date = timestampToDate(timestamp);
  if (!date) {
    return 'Unknown';
  }

  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};
